import {
  MouseEventHandler, ReactNode, useState,
} from 'react';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import {
  Avatar, Divider, IconButton, ListItem, ListItemIcon, ListItemText, Menu, MenuItem,
} from '@mui/material';
import { useHistory } from 'react-router-dom';

import { PAGE_USER_PROFILE } from 'containers/AppRouter';
import { AuthState, useAuth } from 'utils/hooks/providers/auth-context';

interface MyProfileButtonProps {
  handleClick: MouseEventHandler<HTMLButtonElement>;
  isOpen: boolean;
  authState: AuthState;
}
function MyProfileButton({ handleClick, isOpen, authState }: MyProfileButtonProps) {
  const avatarValue = (
    authState.fullname?.charAt(0).toUpperCase() || authState.username?.charAt(0).toUpperCase()
  );

  return (
    <IconButton
      onClick={handleClick}
      aria-controls={isOpen ? 'account-menu' : undefined}
      aria-expanded={isOpen ? 'true' : undefined}
      aria-haspopup="true"
      size="medium"
      color="primary"
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: 'primary.main',
        }}
      >
        { avatarValue }
      </Avatar>
    </IconButton>
  );
}

interface MyMenuProps {
  anchorEl: Element | null;
  isOpen: boolean;
  handleClose: () => void;
  children: any | Element[];
}
function MyMenu({
  anchorEl,
  isOpen,
  handleClose,
  children,
}: MyMenuProps) {
  const arrowStyle: object = {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 0,
    right: 18,
    width: 10,
    height: 10,
    bgcolor: 'background.paper',
    transform: 'translateY(-50%) rotate(45deg)',
    zIndex: 0,
  };

  return (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={isOpen}
      onClose={handleClose}
      onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '&:before': arrowStyle,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      { children }
    </Menu>
  );
}

interface ItemProps {
  icon?: ReactNode;
  title: string | null;
  subtitle?: string | null;
}
function Item({ icon, title, subtitle }: ItemProps) {
  return (
    <ListItem>
      <ListItemIcon style={{ minWidth: '36px' }}>
        { icon }
      </ListItemIcon>
      <ListItemText
        primary={title}
        secondary={subtitle}
      />
    </ListItem>
  );
}

function UserSection() {
  const { authState } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const history = useHistory();

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <MyProfileButton
        handleClick={handleClick}
        isOpen={isOpen}
        authState={authState}
      />
      <MyMenu
        anchorEl={anchorEl}
        isOpen={isOpen}
        handleClose={handleClose}
      >
        <MenuItem onClick={() => history.push(PAGE_USER_PROFILE)}>
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          My account
        </MenuItem>
        <Divider />
        <Item
          icon={<PersonIcon fontSize="small" />}
          title={authState.fullname || authState.username}
          subtitle={authState.fullname ? authState.username : ''}
        />
        <Item
          icon={<AdminPanelSettingsIcon fontSize="small" />}
          title={authState.admin ? 'Administrator' : 'Operator'}
        />
        {
          authState.multi
            ? (
              <Item
                icon={<DynamicFeedIcon fontSize="small" />}
                title="Multi Org"
              />
            )
            : null
        }
        {
            authState.organization
              ? (
                <Item
                  icon={<CorporateFareIcon fontSize="small" />}
                  title={authState.organization}
                />
              )
              : null
          }
      </MyMenu>
    </>
  );
}

export default UserSection;
